// --- EvoMLRoadmapAdvanced.js ---
// Comprehensive, optimized, and agentic-enabled EvoML roadmap with advanced filtering, search, and insightful roadmap structure.
// Integrates advanced agentic/Artemis plans, LLMs, and multi-cloud/chat, combining best features and improvements from both inputs.

import React, { useState, useMemo, useRef } from 'react';
import html2canvas from 'html2canvas';
import './EvoMLRoadmap.css';

// High-level roadmap data for presentations
export const highLevelRoadmapData = [
  {
    id: 'q1-foundation',
    quarter: 'Q1 2025',
    title: '🔗 Proven ML Automation Foundation',
    description: 'Established end-to-end automated ML workflow (see: evoml-automated-workflow) with agent integration, code generation, and enterprise-grade capabilities.',
    highlights: ['End-to-End ML Automation', 'Agent Integration (Vertex AI, Claude Code, Artemis)', 'Production-Ready Code Generation'],
    status: 'Completed'
  },
  {
    id: 'q2-intelligence',
    quarter: 'Q2 2025', 
    title: '🧠 Enhanced GenAI & Governance',
    description: 'Enhanced platform with advanced GenAI capabilities, automated reporting, intelligent governance, and expanded agent ecosystem for superior ML outcomes.',
    highlights: ['Advanced GenAI Integration', 'Automated Audit Reports','Intelligent Model Governance',  'Enhanced Agent Capabilities',  'Enterprise Security & Audit'],
    status: 'In Progress'
  },
  {
    id: 'q3-autonomy',
    quarter: 'Q3 2025',
    title: '🎯 World-Class AI Data Scientist', 
    description: 'Complete essential ML capabilities (unsupervised learning, individual explainability) + advanced GenAI/agentic features to become the world\'s best AI data scientist.',
    highlights: ['Unsupervised Learning Models', 'Individual Explainability (SHAP, LIME)', 'Conversational Data Analysis', 'Agentic Monitoring % Guardrails'],
    status: 'Planned'
  },
  {
    id: 'q4-ecosystem',
    quarter: 'Q4 2025',
    title: '🚀 Automated ML Application Ecosystem',
    description: 'Revolutionary automated app generation: upload dataset → complete ML application with insights, models, and predictions. Full ML ecosystem integration.',
    highlights: ['Dataset-to-App Automation', 'Automated Insights & Correlations', 'Ready-to-Use ML Models', 'Ready to use Data Science Apps'],
    status: 'Planned'
  }
];

// ---- Roadmap Data ----
export const evomlRoadmapData = [
  // Q1 2025 - Foundation Based on Proven Automated Workflow
  {
    id: 'q1-automated-workflow-foundation',
    quarter: 'Q1 2025',
    title: 'Proven End-to-End ML Automation Workflow',
    description:
      "Established comprehensive automated ML workflow covering data preparation, model building, governance, deployment, and monitoring - as demonstrated in our evoml-automated-workflow.",
    keyPoints: [
      "✅ Automated data preparation with Artemis code generation",
      "✅ Automated model building with 100+ algorithms tested",
      "✅ Model governance with automated validation reports",
      "✅ One-click deployment with production-ready code",
      "✅ MLOps monitoring with continuous optimization",
      "✅ Personalized application generation",
    ],
    status: 'Completed'
  },
  {
    id: 'q1-agent-ecosystem-foundation',
    quarter: 'Q1 2025',
    title: 'Multi-Agent Integration Ecosystem',
    description:
      "Established agent integration platform enabling seamless connection with Vertex AI, Claude Code, and Artemis agents through our automated workflow.",
    keyPoints: [
      "✅ Vertex AI agent integration for cloud-scale deployment",
      "✅ Claude Code agent for local development and testing",
      "✅ Artemis agent for intelligent code evolution and optimization",
      "✅ Model Context Protocol (MCP) for agent communication",
      "✅ Agent orchestration within the automated workflow",
      "✅ Complete audit trails for all agent interactions",
    ],
    status: 'Completed'
  },
  {
    id: 'q1-production-ready-code',
    quarter: 'Q1 2025',
    title: 'Production-Ready Code Generation & Deployment',
    description:
      'Automated generation of clean, production-ready Python code with no dependencies, enabling seamless integration into existing enterprise environments.',
    keyPoints: [
      "✅ Pure Python code generation with zero external dependencies",
      "✅ Enterprise-grade security and access controls",
      "✅ Automated model validation and testing frameworks",
      "✅ RESTful API generation for model serving",
      "✅ Integration with major cloud platforms (AWS, Azure, GCP)",
      "✅ Comprehensive documentation and deployment guides",
    ],
    status: 'Completed'
  },

  // Q2 2025 - Enhanced ML Workflow Capabilities
  {
    id: 'q2-enhanced-genai-integration',
    quarter: 'Q2 2025',
    title: 'Advanced GenAI Integration & Automated Reporting',
    description:
      "Enhanced GenAI capabilities integrated throughout the ML workflow, enabling intelligent automation, conversational interaction, and automated audit-ready reporting.",
    keyPoints: [
      "🧠 Advanced GenAI integration for intelligent model insights",
      "📋 Automated audit report generation with custom templates",
      "💬 GenAI-powered conversational interfaces for data exploration",
      "🎯 Intelligent feature recommendation and automated exclusion",
      "📊 Enhanced model explainability with natural language descriptions",
      "⚖️ Automated compliance validation and regulatory reporting",
    ],
    status: 'In Progress'
  },
  {
    id: 'q2-advanced-agent-capabilities',
    quarter: 'Q2 2025',
    title: 'Enhanced Agent Orchestration & Governance',
    description:
      'Advanced agent capabilities with improved orchestration, decision transparency, and comprehensive governance frameworks for enterprise deployment.',
    keyPoints: [
      "🤖 Enhanced agent coordination with intelligent decision-making",
      "📊 Real-time agent performance monitoring and optimization",
      "🔒 Advanced agent access controls and permission management",
      "⚖️ Complete agent decision transparency and audit trails",
      "🎯 Intelligent agent conflict resolution and escalation",
      "📋 Automated agent compliance validation and reporting",
    ],
    status: 'In Progress'
  },
  {
    id: 'q2-enhanced-deployment-monitoring',
    quarter: 'Q2 2025',
    title: 'Enhanced Model Deployment & Production Monitoring',
    description:
      'Advanced deployment capabilities with intelligent monitoring, automated governance controls, and enhanced production model management.',
    keyPoints: [
      "🚀 Enhanced deployment workflows with governance controls",
      "📊 Advanced model monitoring with predictive alerting",
      "🔄 Intelligent automated model retraining with validation",
      "🎯 Advanced A/B testing with statistical significance analysis",
      "📈 Production model performance optimization recommendations",
      "⚖️ Enhanced regulatory compliance validation for all deployments",
    ],
    status: 'In Progress'
  },

  // Q3 2025 - Becoming the World-Class AI Data Scientist
  {
    id: 'q3-essential-ml-capabilities',
    quarter: 'Q3 2025',
    title: 'Complete Essential ML Capabilities',
    description:
      'Complete the essential ML capabilities that EvoML currently lacks: unsupervised learning models and advanced individual explainability to become a comprehensive ML platform.',
    keyPoints: [
      "🔍 Unsupervised learning models (clustering, anomaly detection, dimensionality reduction)",
      "👤 Individual explainability with SHAP and LIME integration",
      "🔄 What-if analysis capabilities for comprehensive stress testing",
      "📊 Advanced pattern discovery and association rule mining",
      "🎯 Comprehensive bias detection and fairness analysis",
      "⚡ Self-organizing maps and neural clustering capabilities",
    ],
    status: 'Planned'
  },
  {
    id: 'q3-conversational-data-scientist',
    quarter: 'Q3 2025',
    title: 'Conversational AI Data Scientist',
    description:
      'Transform EvoML into a conversational AI data scientist that users can talk to, ask questions, and get intelligent insights about their data and models.',
    keyPoints: [
      "💬 Talk to your data - conversational data exploration and analysis",
      "🧠 Intelligent automatic feature recommendation and exclusion",
      "📋 Conversational model explanation and interpretation",
      "🎯 Natural language querying of model performance and insights",
      "📊 Intelligent data storytelling and insight generation",
      "🔍 Conversational debugging and model improvement suggestions",
    ],
    status: 'Planned'
  },
  {
    id: 'q3-advanced-genai-automation',
    quarter: 'Q3 2025',
    title: 'Advanced GenAI Automation & Synthetic Data',
    description:
      'Advanced GenAI capabilities for synthetic data generation, automated report creation, and intelligent ML workflow optimization.',
    keyPoints: [
      "🧬 Synthetic data generation for rapid prototyping and testing",
      "📄 Auto-generate comprehensive model reports with custom templates",
      "🤖 Agentic chat capabilities integrated throughout EvoML workflow",
      "🎯 Intelligent workflow optimization based on data characteristics",
      "📊 Automated insight generation and anomaly detection",
      "⚡ GenAI-powered feature engineering and model selection",
    ],
    status: 'Planned'
  },
  {
    id: 'q3-mcp-agentic-ecosystem',
    quarter: 'Q3 2025',
    title: 'MCP Server & Advanced Agentic Capabilities',
    description:
      'Establish EvoML as an MCP server and enhance agentic capabilities for intelligent coordination with external ML tools and platforms.',
    keyPoints: [
      "🔌 EvoML MCP server for seamless integration with external agents",
      "🤖 Advanced agentic capabilities with intelligent decision-making",
      "🔄 Autonomous agent coordination with Artemis for code optimization",
      "📊 Intelligent agent orchestration for complex ML workflows",
      "🎯 Proactive model monitoring and optimization agents",
      "⚖️ Complete audit trails and explainability for all agent decisions",
    ],
    status: 'Planned'
  },

  // Q4 2025 - Revolutionary Dataset-to-Application Automation
  {
    id: 'q4-automated-app-generation',
    quarter: 'Q4 2025',
    title: 'Revolutionary Dataset-to-App Automation',
    description:
      'Revolutionary capability: upload any dataset and automatically generate a complete ML application with data insights, hidden correlations, and ready-to-use prediction models.',
    keyPoints: [
      "📤 Upload dataset → Complete ML application generation",
      "🔍 Automated discovery of hidden insights and data correlations",
      "📊 Comprehensive data profiling and statistical analysis",
      "🤖 Ready-to-use ML models for predictions and classification",
      "📱 Interactive dashboards and visualization interfaces",
      "⚡ One-click deployment of generated applications",
    ],
    status: 'Planned'
  },
  {
    id: 'q4-intelligent-data-insights',
    quarter: 'Q4 2025',
    title: 'Intelligent Data Insights & Pattern Discovery',
    description:
      'Advanced AI-powered data analysis that automatically discovers patterns, anomalies, and insights that human data scientists might miss.',
    keyPoints: [
      "🧠 AI-powered hidden pattern discovery and correlation analysis",
      "📈 Automated trend analysis and forecasting capabilities",
      "🎯 Intelligent feature discovery and relationship mapping",
      "🚨 Automated anomaly detection and outlier identification",
      "💡 GenAI-powered data storytelling and insight narratives",
      "📊 Comprehensive data quality assessment and recommendations",
    ],
    status: 'Planned'
  },
  {
    id: 'q4-mcp-data-ecosystem',
    quarter: 'Q4 2025',
    title: 'Universal Data Integration via MCP Servers',
    description:
      'Comprehensive data ecosystem integration through MCP servers, enabling seamless connection to any data source and automated data pipeline creation.',
    keyPoints: [
      "🔌 MCP server integrations for unlimited data source connectivity",
      "🗄️ Automated integration with databases, APIs, and cloud storage",
      "🌊 Real-time data streaming and batch processing capabilities",
      "🔄 Intelligent data transformation and normalization",
      "📡 Cross-platform data synchronization and management",
      "⚡ Automated data pipeline creation and optimization",
    ],
    status: 'Planned'
  },
  {
    id: 'q4-ml-ecosystem-integration',
    quarter: 'Q4 2025',
    title: 'Comprehensive ML Ecosystem Integration',
    description:
      'Agentic connectivity with all major ML tools and platforms, creating a unified ML ecosystem with EvoML at the center.',
    keyPoints: [
      "☁️ Agentic integration with Vertex AI for advanced deployment options",
      "📊 MLOps monitoring integration with comprehensive observability",
      "⚖️ Auditability tools integration for complete compliance tracking",
      "🔄 Automated model versioning and experiment tracking",
      "🤖 Intelligent agent coordination across the ML ecosystem",
      "🌐 Universal ML workflow orchestration and management",
    ],
    status: 'Planned'
  }
];

// ---- Advanced Roadmap Component ----

// Helper function to generate and download markdown
const downloadMarkdown = (data, title) => {
  let markdown = `# ${title || "EvoML Roadmap"}\n\n`;

  const quarters = ['Q1 2025', 'Q2 2025', 'Q3 2025', 'Q4 2025'];

  quarters.forEach((quarter, index) => {
    const itemsInQuarter = data.filter(item => item.quarter === quarter);
    if (itemsInQuarter.length > 0) {
      markdown += `## ${quarter}\n\n`;
      itemsInQuarter.forEach((item, itemIndex) => {
        markdown += `### ${item.title}\n`;
        markdown += `**Status:** ${item.status}\n\n`;
        markdown += `_${item.description}_\n\n`;
        if (item.keyPoints) {
          markdown += `**Key Points:**\n`;
          item.keyPoints.forEach(point => {
            markdown += `  - ${point}\n`;
          });
        } else if (item.highlights) {
          markdown += `**Highlights:**\n`;
          item.highlights.forEach(highlight => {
            markdown += `  - ${highlight}\n`;
          });
        }
        markdown += `\n`;
        if (itemIndex < itemsInQuarter.length - 1) {
          markdown += `--- \n\n`; // Horizontal rule between items in the same quarter
        }
      });
      if (index < quarters.length - 1) {
          markdown += `***\n\n`; // Stronger horizontal rule between quarters
      }
    }
  });

  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'evoml-roadmap.md';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Util for highlighting search term in results
function highlightWithMark(text, searchTerm) {
  if (!searchTerm || typeof text !== 'string') return text;
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.split(regex).map((part, i) =>
    regex.test(part) ? <mark key={i}>{part}</mark> : part
  );
}

// Tag extraction for filtering (automatic based on keywords in id, title, or description)
function extractTags({ id, title, description }) {
  const tags = [];
  const all = (id + ' ' + title + ' ' + description).toLowerCase();
  if (all.includes('agent') || all.includes('agentic') || all.includes('autonomous')) tags.push('agentic');
  if (all.includes('genai') || all.includes('llm') || all.includes('language model') || all.includes('conversational')) tags.push('genai');
  if (all.includes('audit') || all.includes('compliance') || all.includes('regulatory') || all.includes('governance')) tags.push('auditability');
  if (all.includes('banking') || all.includes('risk') || all.includes('financial') || all.includes('credit')) tags.push('banking');
  if (all.includes('api') || all.includes('sdk') || all.includes('integration')) tags.push('integration');
  if (all.includes('artemis') || all.includes('code evolution') || all.includes('optimization')) tags.push('artemis');
  if (all.includes('cloud') || all.includes('deployment') || all.includes('enterprise')) tags.push('enterprise');
  if (all.includes('explainability') || all.includes('bias') || all.includes('fairness') || all.includes('what-if')) tags.push('explainability');
  if (all.includes('monitor') || all.includes('real-time') || all.includes('alert')) tags.push('monitoring');
  // Allow overlapping tags for better filtering
  return tags;
}

// Roadmap Item
const EvoMLRoadmapItem = ({ item, isFirst, isLast, highlightTerm }) => {
  const tags = useMemo(() => extractTags(item), [item]);
  const hasHighlight = highlightTerm &&
    (item.title.toLowerCase().includes(highlightTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(highlightTerm.toLowerCase()) ||
      item.keyPoints.some(point => point.toLowerCase().includes(highlightTerm.toLowerCase())));
  return (
    <div className={`evoml-roadmap-item-container ${hasHighlight ? 'highlight' : ''}`}>
      <div className="evoml-roadmap-item-timeline">
        <div className={`evoml-roadmap-item-dot ${item.status.toLowerCase().replace(/\s+/g, '-')}`}></div>
        {!isLast && <div className="evoml-roadmap-item-line"></div>}
      </div>
      <div className="evoml-roadmap-item-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, flex: 1 }}>
            {highlightWithMark(item.quarter, highlightTerm)} - {highlightWithMark(item.title, highlightTerm)}
          </h3>
          {tags.map(tag =>
            <span key={tag} className={`evoml-tag evoml-tag-${tag}`}>{tag.replace('-', ' ')}</span>
          )}
        </div>
        <p>{highlightWithMark(item.description, highlightTerm)}</p>
        <ul>
          {item.keyPoints.map((point, idx) =>
            <li key={idx}>{highlightWithMark(point, highlightTerm)}</li>
          )}
        </ul>
        <span className={`evoml-roadmap-item-status ${item.status.toLowerCase().replace(/\s+/g, '-')}`}>{item.status}</span>
      </div>
    </div>
  );
};

// Filtering & Search UI
const FILTER_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'agentic', label: '🤖 Agents' },
  { id: 'genai', label: '🧠 GenAI' },
  { id: 'auditability', label: '⚖️ Auditability' },
  { id: 'banking', label: '🏦 Banking' },
  { id: 'integration', label: '🔗 Integration' },
  { id: 'artemis', label: '🔄 Artemis' },
  { id: 'enterprise', label: '🌐 Enterprise' },
  { id: 'explainability', label: '🔍 Explainability' },
  { id: 'monitoring', label: '📊 Monitoring' }
];

const RoadmapAdvancedFilter = ({
  activeFilter, setFilter, searchTerm, setSearch, onDownloadMarkdown
}) => (
  <div className="evoml-roadmap-filter">
    <div className="evoml-filter-buttons">
      {FILTER_CATEGORIES.map(cat => (
        <button
          key={cat.id}
          className={`filter-button ${activeFilter === cat.id ? 'active' : ''}`}
          onClick={() => setFilter(cat.id)}
        >
          {cat.label}
        </button>
      ))}
    </div>
    <div className="evoml-search">
      <input
        type="text"
        placeholder="Search roadmap for any tech, agent, or capability..."
        value={searchTerm}
        onChange={e => setSearch(e.target.value)}
        aria-label="Search roadmap"
      />
      {searchTerm && (
        <button className="clear-search" onClick={() => setSearch('')}>
          ×
        </button>
      )}
    </div>
    <button onClick={onDownloadMarkdown} className="download-markdown-button">
      Download Markdown
    </button>
  </div>
);

// Helper function to download table as PNG
const downloadTableAsPNG = async (tableRef, filename = 'evoml-roadmap-table') => {
  if (!tableRef.current) return;
  
  try {
    const canvas = await html2canvas(tableRef.current, {
      backgroundColor: '#ffffff',
      scale: 2, // Higher quality
      useCORS: true,
      allowTaint: true
    });
    
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL();
    link.click();
  } catch (error) {
    console.error('Error generating PNG:', error);
    alert('Error generating PNG. Please try again.');
  }
};

// Table Component for High-Level Roadmap
const RoadmapTable = ({ data, tableRef }) => {
  const getStatusColor = (status) => {
    switch(status.toLowerCase()) {
      case 'completed': return '#4caf50';
      case 'in progress': return '#ff9800';
      case 'planned': return '#2196f3';
      default: return '#757575';
    }
  };

  return (
    <div ref={tableRef} className="roadmap-table-container">
      <div className="table-header">
        <h2>EvoML 2025 Roadmap - High-Level Overview</h2>
        <p className="table-subtitle">Streamlined view of EvoML's evolution aligned with our automated workflow capabilities</p>
      </div>
      <table className="roadmap-table">
        <thead>
          <tr>
            <th>Quarter</th>
            <th>Status</th>
            <th>Title</th>
            <th>Description</th>
            <th>Key Highlights</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={item.id} className={`status-${item.status.toLowerCase().replace(/\s+/g, '-')}`}>
              <td className="quarter-cell">
                <strong>{item.quarter}</strong>
              </td>
              <td className="status-cell">
                <span 
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(item.status) }}
                />
                <span className="status-text">{item.status}</span>
              </td>
              <td className="title-cell">
                <strong>{item.title}</strong>
              </td>
              <td className="description-cell">
                {item.description}
              </td>
              <td className="highlights-cell">
                <div className="highlights-list">
                  {item.highlights.map((highlight, idx) => (
                    <div key={idx} className="highlight-item">• {highlight}</div>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// High-level Roadmap Item Component
const HighLevelRoadmapItem = ({ item, index, total }) => {
  const getStatusColor = (status) => {
    switch(status.toLowerCase()) {
      case 'completed': return '#4caf50';
      case 'in progress': return '#ff9800';
      case 'planned': return '#2196f3';
      default: return '#757575';
    }
  };

  return (
    <div className="high-level-roadmap-item">
      <div className="roadmap-timeline">
        <div 
          className="roadmap-dot"
          style={{ backgroundColor: getStatusColor(item.status) }}
        />
        {index < total - 1 && <div className="roadmap-line" />}
      </div>
      <div className="roadmap-content">
        <div className="roadmap-header">
          <h3>{item.quarter}</h3>
          <span 
            className="status-badge"
            style={{ backgroundColor: getStatusColor(item.status) }}
          >
            {item.status}
          </span>
        </div>
        <h4>{item.title}</h4>
        <p>{item.description}</p>
        <div className="highlights">
          {item.highlights.map((highlight, idx) => (
            <span key={idx} className="highlight-tag">{highlight}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Roadmap Component
const EvoMLRoadmapAdvanced = ({ title }) => {
  const [viewMode, setViewMode] = useState('high-level'); // 'high-level', 'table', or 'detailed'
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const tableRef = useRef(null);

  // Group items by quarter in correct order
  const quarters = useMemo(
    () => ['Q1 2025', 'Q2 2025', 'Q3 2025', 'Q4 2025'],
    []
  );

  // Filter logic: by tag and then by search
  const filteredData = useMemo(() => {
    return evomlRoadmapData.filter(item => {
      const tags = extractTags(item);

      // By category filter
      if (activeFilter !== 'all' && !tags.includes(activeFilter)) {
        return false;
      }
      // By search
      if (searchTerm) {
        const haystack = (
          item.title + ' ' +
          item.description + ' ' +
          item.keyPoints.join(' ')
        ).toLowerCase();
        if (!haystack.includes(searchTerm.toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  }, [activeFilter, searchTerm]);

  // Organize by quarter for display
  const itemsByQuarter = useMemo(() =>
    quarters.map(quarter => ({
      quarter,
      items: filteredData.filter(item => item.quarter === quarter)
    }))
    , [filteredData, quarters]);

  const hasResults = itemsByQuarter.some(q => q.items.length > 0);

  return (
    <div className="evoml-roadmap-container">
      <h2 className="evoml-roadmap-title">
        {title || "EvoML: 2025 Roadmap"}
      </h2>
      
      {/* View Toggle */}
      <div className="view-toggle">
        <button 
          className={`toggle-btn ${viewMode === 'high-level' ? 'active' : ''}`}
          onClick={() => setViewMode('high-level')}
        >
          📊 High-Level View
        </button>
        <button 
          className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
          onClick={() => setViewMode('table')}
        >
          📋 Table View
        </button>
        <button 
          className={`toggle-btn ${viewMode === 'detailed' ? 'active' : ''}`}
          onClick={() => setViewMode('detailed')}
        >
          🔍 Detailed View
        </button>
      </div>

      {/* High-Level View */}
      {viewMode === 'high-level' && (
        <div className="high-level-roadmap">
          <p className="roadmap-subtitle">
            A streamlined view of EvoML's evolution aligned with our automated workflow capabilities
          </p>
          <div className="high-level-timeline">
            {highLevelRoadmapData.map((item, index) => (
              <HighLevelRoadmapItem 
                key={item.id} 
                item={item} 
                index={index}
                total={highLevelRoadmapData.length}
              />
            ))}
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="table-view">
          <div className="table-controls">
            <button 
              onClick={() => downloadTableAsPNG(tableRef, 'evoml-roadmap-table')}
              className="download-png-button"
            >
              📥 Download as PNG
            </button>
          </div>
          <RoadmapTable data={highLevelRoadmapData} tableRef={tableRef} />
        </div>
      )}

      {/* Detailed View */}
      {viewMode === 'detailed' && (
        <>
          <RoadmapAdvancedFilter
            activeFilter={activeFilter}
            setFilter={setActiveFilter}
            searchTerm={searchTerm}
            setSearch={setSearchTerm}
            onDownloadMarkdown={() => downloadMarkdown(viewMode === 'high-level' ? highLevelRoadmapData : evomlRoadmapData, title)}
          />
          {!hasResults && (
            <div className="evoml-no-results">
              <p>No roadmap items match your current filter/search.</p>
              <button onClick={() => { setActiveFilter('all'); setSearchTerm(''); }}>
                Reset Filters
              </button>
            </div>
          )}
          <div className="evoml-roadmap-timeline-view">
            {itemsByQuarter.map(({ quarter, items }, quarterIndex) => (
              items.length > 0 && (
                <div key={quarter} className="evoml-roadmap-quarter-column">
                  <h4>{quarter}</h4>
                  <div className="evoml-roadmap-quarter-items">
                    {items.map((item, itemIndex) => (
                      <EvoMLRoadmapItem
                        key={item.id}
                        item={item}
                        isFirst={itemIndex === 0 && quarterIndex === 0}
                        isLast={itemIndex === items.length - 1 && quarterIndex === itemsByQuarter.filter(q => q.items.length > 0).length - 1}
                        highlightTerm={searchTerm}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default EvoMLRoadmapAdvanced;

// --- EvoMLRoadmap.css ---
// This content has been moved to EvoMLRoadmap.css